import type { Reference, Task } from '@medplum/fhirtypes'
import { isSubsetOfCodeableConcepts } from '@seen/core/codeableConcept'
import { isInPast } from '@seen/core/dates'
import { invariant } from '@seen/core/invariant'
import { type InboxItem, InboxItemType } from '@seen/core/models/inbox'
import { DocumentReviewTask } from '@seen/core/models/tasks/standard/documentReviewTask'
import { StandardTask } from '@seen/core/models/tasks/standard/standardTask'
import { resolveId } from '@seen/core/references'
import { isOfType } from '@seen/core/utils'
import {
  type UseQueryResult,
  useQueries,
  useQueryClient,
} from '@tanstack/react-query'
import { chunk } from 'lodash-es'
import { useCallback, useMemo } from 'react'
import { cacheKeys } from '#src/hooks/cacheKeys.ts'
import {
  getCategoryForPartOfResourceType,
  type InboxCategoryId,
  isCommentType,
  isTaskBasedType,
  matchesMedicationHeuristic,
  TYPE_BASED_CATEGORY_MAP,
} from '#src/hooks/inbox/inboxCategories.ts'
import { useMedplum } from '#src/hooks/useMedplum.ts'
import type { NotificationItem } from '#src/hooks/useNotificationItems.ts'
import { useQuery } from '#src/hooks/useQuery.ts'

function getTaskReference(item: InboxItem): Reference<Task> | undefined {
  if (
    item.type === InboxItemType.TaskAssigned ||
    item.type === InboxItemType.TaskDue ||
    item.type === InboxItemType.TaskNewComment
  ) {
    return item.task
  }
  return undefined
}

export interface CategorizedItems {
  /* All items with their resolved category */
  categoryMap: Map<string, InboxCategoryId>
  /* Item IDs that have stat priority (dual-categorized) */
  statItemIds: Set<string>
  /* Item IDs that are overdue (dual-categorized) */
  overdueItemIds: Set<string>
  /* Item IDs whose underlying Task is completed or cancelled.
   * Used to exclude stale notifications for already-done tasks. */
  completedTaskItemIds: Set<string>
  /* Whether task data is still loading for categorization */
  isResolving: boolean
}

/*
 * Whether an item belongs to a category. 'stat' and 'overdue' are
 * dual-categorized via their own sets; everything else matches its primary
 * category. Callers derive both the displayed rows and the counts through this
 * single predicate so the list and the category bar can never disagree.
 */
export function itemMatchesCategory(
  itemId: string,
  category: InboxCategoryId,
  {
    categoryMap,
    statItemIds,
    overdueItemIds,
  }: Pick<CategorizedItems, 'categoryMap' | 'statItemIds' | 'overdueItemIds'>
): boolean {
  if (category === 'all') {
    return true
  }
  if (category === 'stat') {
    return statItemIds.has(itemId)
  }
  if (category === 'overdue') {
    return overdueItemIds.has(itemId)
  }
  return categoryMap.get(itemId) === category
}

/*
 * Task resolution: categorization needs each task's Task resource (focus,
 * code, dueOn, completion state). Rather than make the membership the cache
 * identity, we observe each task's canonical readExtended cache entry — the
 * same key useRead(StandardTask, …) reads and usePatchMutation writes — and
 * batch-fetch only the ids not yet cached. This means archiving a row drops
 * one observer and refetches nothing (the rest stay cached, so the category
 * view never blanks), a task mutation re-renders us with fresh state, and we
 * never duplicate the per-task fetches that rows/detail panels already make.
 */
const BATCH_CHUNK_SIZE = 50
const TASK_STALE_TIME = 5 * 60 * 1000

export function useInboxCategoryFilter(
  items: NotificationItem[]
): CategorizedItems {
  const medplum = useMedplum()
  const queryClient = useQueryClient()

  /* Collect task-based items and their unique IDs */
  const taskItemsToResolve = useMemo(
    () => items.filter((item) => isTaskBasedType(item.data.type)),
    [items]
  )

  /* Build stable map of notificationId -> taskId */
  const notifToTaskId = useMemo(() => {
    const map = new Map<string, string>()
    for (const item of taskItemsToResolve) {
      const ref = getTaskReference(item.data)
      if (!ref) {
        continue
      }
      const id = resolveId(ref)
      if (id) {
        map.set(item.id, id)
      }
    }
    return map
  }, [taskItemsToResolve])

  /* Stable query key: sorted unique task IDs */
  const taskIds = useMemo(
    () => [...new Set(notifToTaskId.values())].sort(),
    [notifToTaskId]
  )

  /* Observe each task's shared per-id cache entry. enabled:false → never issue
     N individual reads; we only read whatever useRead / the fill below have
     already cached. combine derives the resolved map plus the ids still
     missing. */
  const combineTaskResults = useCallback(
    (results: UseQueryResult<StandardTask>[]) => {
      const map = new Map<string, StandardTask>()
      const missing: string[] = []
      taskIds.forEach((id, index) => {
        const task = results[index]?.data
        if (task) {
          map.set(id, task)
        } else {
          missing.push(id)
        }
      })
      return { taskMap: map, missingIds: missing }
    },
    [taskIds]
  )

  const { taskMap, missingIds } = useQueries({
    queries: taskIds.map((id) => ({
      queryKey: cacheKeys.readExtended(
        StandardTask.RESOURCE_TYPE,
        id,
        StandardTask.name
      ),
      queryFn: () => medplum.readResourceClass(StandardTask, id),
      enabled: false,
      staleTime: TASK_STALE_TIME,
    })),
    combine: combineTaskResults,
  })

  /* Fill only the uncached ids, in batches, seeding the same per-task caches
     the observers above (and useRead) read from. Keyed by the missing set, so
     it runs only when genuinely-new ids appear — never on a removal. */
  const { isFetching: isResolving } = useQuery({
    queryKey: [
      'inbox',
      'category-filter-task-fill',
      missingIds.join(','),
    ] as const,
    queryFn: async () => {
      for (const ids of chunk(missingIds, BATCH_CHUNK_SIZE)) {
        const tasks = await medplum.searchResourceClass(StandardTask, {
          _id: ids.join(','),
          _count: BATCH_CHUNK_SIZE,
        })
        for (const task of tasks) {
          if (!task.id) {
            continue
          }
          queryClient.setQueryData(
            cacheKeys.readExtended(
              StandardTask.RESOURCE_TYPE,
              task.id,
              StandardTask.name
            ),
            task
          )
          queryClient.setQueryData(
            cacheKeys.read(StandardTask.RESOURCE_TYPE, task.id),
            task.toFhirResource()
          )
        }
      }
      return null
    },
    enabled: missingIds.length > 0,
    staleTime: TASK_STALE_TIME,
  })

  /* Build category map (item.id -> primary category), stat set, and overdue set */
  const { categoryMap, statItemIds, overdueItemIds, completedTaskItemIds } =
    useMemo(() => {
      const map = new Map<string, InboxCategoryId>()
      const statIds = new Set<string>()
      const overdueIds = new Set<string>()
      const completedIds = new Set<string>()

      for (const item of items) {
        /* 1. Pure type-based categories (OrderSigned, NewProposedAppointment) */
        const typeBased = TYPE_BASED_CATEGORY_MAP[item.data.type]
        if (typeBased) {
          map.set(item.id, typeBased)
          continue
        }

        /* 2. Comment types — use partOfResourceType from backend enrichment */
        if (isCommentType(item.data)) {
          map.set(
            item.id,
            getCategoryForPartOfResourceType(item.data.partOfResourceType)
          )
          continue
        }

        /* 3. Task types — check focus (ServiceRequest) and code (document-review) */
        if (isTaskBasedType(item.data.type)) {
          const taskId = notifToTaskId.get(item.id)
          const task = taskId ? taskMap?.get(taskId) : undefined

          /* Track stat-priority items (dual-categorized: appear in stat AND primary)
             Scoped to task-based types; comment types would need related task resolution to include here */
          if (task?.isStatPriority) {
            statIds.add(item.id)
          }

          /* Track overdue items: task has a due date in the past and is not completed/cancelled */
          if (
            task?.dueOn &&
            isInPast(task.dueOn) &&
            !task.isCompletedOrCancelled
          ) {
            overdueIds.add(item.id)
          }

          /* Track items whose underlying task is already done/cancelled —
             counters should not include these stale notifications. */
          if (task?.isCompletedOrCancelled) {
            completedIds.add(item.id)
          }

          if (task && isOfType(task.focus, 'ServiceRequest')) {
            map.set(item.id, 'orders')
          } else if (
            task &&
            isSubsetOfCodeableConcepts(DocumentReviewTask.code, task.code)
          ) {
            map.set(item.id, 'documents')
          } else if (
            task &&
            matchesMedicationHeuristic(task.title, task.description)
          ) {
            /* Heuristic medication grouping: only catches tasks that would
               otherwise fall through to 'other-tasks' (orders/documents win). */
            map.set(item.id, 'medications')
          } else {
            /* Default task-based items to 'other-tasks' (including while loading) */
            map.set(item.id, 'other-tasks')
          }
          continue
        }

        /* Exhaustive check — all InboxItemType branches handled above */
        invariant(false, `Unexpected inbox item type: ${item.data.type}`)
      }

      return {
        categoryMap: map,
        statItemIds: statIds,
        overdueItemIds: overdueIds,
        completedTaskItemIds: completedIds,
      }
    }, [items, notifToTaskId, taskMap])

  return {
    categoryMap,
    statItemIds,
    overdueItemIds,
    completedTaskItemIds,
    isResolving,
  }
}
